import MailListItemPartIconBar from './MailListItemPartIconBar';
import getFirstNodeIdToShow from '../selectors/getFirstNodeIdToShow';
import getItemPartTimeText from '../selectors/getItemPartTimeText';
import busStopStateToIconUrlConverter from '../utils/busStopStateToIconUrlConverter';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Check } from 'office-ui-fabric-react/lib/Check';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import * as mailListSelectionActionsV2 from 'owa-mail-actions/lib/mailListSelectionActions';
import { getStore as getListViewStore } from 'owa-mail-list-store/lib/store/Store';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import Message from 'owa-service/lib/contract/Message';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import {
    getMailListItemPartDragPreview,
    MAIL_ITEM_DRAG_XOFFSET,
    MAIL_ITEM_DRAG_YOFFSET,
} from '../utils/mailListDragUtil';
import { getIsBitSet, ListViewBitFlagsMasks } from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import DisplayDensityMode from 'owa-service/lib/contract/DisplayDensityMode';

const styles = require('./MailListItemPart.scss');
const classNames = require('classnames/bind');

export interface MailListItemPartProps {
    allNodeIds: string[];
    nodeId: string;
    tableViewId: string;
    isSingleLine: boolean;
    tableViewSupportsFlagging: boolean;
    densityMode: DisplayDensityMode;
}

class MailListItemPartComputedState {
    constructor(private tableViewId: string, private nodeId: string) {}

    @computed
    get showBusStop(): boolean {
        const expandedConversationViewState = getListViewStore().expandedConversationViewState;

        // Show bus stop state when expanded conversation has single selection
        return expandedConversationViewState.selectedNodeIds.length === 1;
    }

    @computed
    get showCheckbox(): boolean {
        const expandedConversationViewState = getListViewStore().expandedConversationViewState;

        // Show checkbox if the current item part is not single selected in expanded conversation
        return !(
            expandedConversationViewState.selectedNodeIds.length === 1 &&
            expandedConversationViewState.selectedNodeIds[0] === this.nodeId
        );
    }

    @computed
    get isChecked(): boolean {
        const expandedConversationViewState = getListViewStore().expandedConversationViewState;

        return (
            expandedConversationViewState.selectedNodeIds.length > 1 &&
            expandedConversationViewState.selectedNodeIds.indexOf(this.nodeId) > -1
        );
    }

    @computed
    get isSelected(): boolean {
        const expandedConversationViewState = getListViewStore().expandedConversationViewState;

        return (
            expandedConversationViewState.selectedNodeIds.length > 0 &&
            expandedConversationViewState.selectedNodeIds.indexOf(this.nodeId) > -1
        );
    }

    @computed
    get showHoverActions(): boolean {
        const tableView = getListViewStore().tableViews.get(this.tableViewId);
        if (!tableView) {
            return false;
        }

        const expandedConversationViewState = getListViewStore().expandedConversationViewState;

        // Show hover actions if
        // - the list view is not in multi-select mode
        // - and the list view is not in virual selected all mode
        // - and the expanded list view is not in multi-select mode
        return (
            tableView.selectedRowKeys.size <= 1 &&
            !tableView.isInVirtualSelectAllMode &&
            expandedConversationViewState.selectedNodeIds.length <= 1
        );
    }
}

@observer
export default class MailListItemPart extends React.Component<MailListItemPartProps, {}> {
    private computedState: MailListItemPartComputedState;
    private backgroundColorClass: string;

    constructor(props: MailListItemPartProps) {
        super(props);
        this.initializeMailListComputedState(props);
    }

    render() {
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        this.backgroundColorClass = this.calculateBackgroundColorClass();
        const shouldBackgroundChangeOnHover =
            !this.computedState.isSelected && !this.computedState.isChecked;
        const tableView = getListViewStore().tableViews.get(this.props.tableViewId);
        let itemPartClasses;
        let itemPartStyles = styles.itemPart;
        if (this.props.densityMode) {
            if (this.props.densityMode === 'Simple') {
                itemPartStyles = styles.itemPartMediumDensity;
            } else if (this.props.densityMode === 'Compact') {
                itemPartStyles = styles.itemPartCompactDensity;
            }
        }

        if (item) {
            itemPartClasses = classNames(
                itemPartStyles,
                this.backgroundColorClass,
                !item.IsRead && styles.unread,
                this.computedState.showHoverActions && styles.showItemPartHoverActionsOnHover,
                shouldBackgroundChangeOnHover && styles.showHoverStyles,
                item.IsDraft && styles.itemPartContentDraft
            );
        }

        return (
            item && (
                <Draggable
                    canDrag={this.canDrag}
                    getDragData={this.getDragData}
                    getDragPreview={getMailListItemPartDragPreview}
                    xOffset={MAIL_ITEM_DRAG_XOFFSET}
                    yOffset={MAIL_ITEM_DRAG_YOFFSET}>
                    <div
                        key={item.ItemId.Id}
                        className={itemPartClasses}
                        onClick={() => {
                            mailListSelectionActionsV2.itemPartClicked(
                                this.props.nodeId,
                                item.ItemId.Id,
                                this.props.allNodeIds,
                                tableView
                            );
                        }}
                        onContextMenu={this.onItemPartContextMenu}>
                        {this.props.isSingleLine
                            ? this.renderSingleLineViewMailListItemPart()
                            : this.renderThreeColumnViewMailListItemPart()}
                    </div>
                </Draggable>
            )
        );
    }

    private renderThreeColumnViewMailListItemPart() {
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        const senderName = item.From && item.From.Mailbox && item.From.Mailbox.Name;
        const timeText = getItemPartTimeText(item);
        const senderClasses = classNames(
            !item.IsRead ? styles.senderUnread : styles.sender,
            item.IsDraft && styles.draft
        );
        const isFirstItemPart = this.props.nodeId == getFirstNodeIdToShow(this.props.allNodeIds);

        return (
            <>
                <div className={styles.mailListItemPartFirstColumnThreeColumnView}>
                    {this.renderFirstColumnContent()}
                </div>
                <div
                    className={classNames(
                        styles.mailListItemPartSecondColumnThreeColumnView,
                        isFirstItemPart && styles.firstItemPartNoTopBorder
                    )}>
                    <span className={senderClasses}>
                        {item.IsDraft ? strings.draftSenderPersonaPlaceholder : senderName}
                    </span>
                </div>
                <div
                    className={classNames(
                        styles.mailListItemPartThirdColumnThreeColumnView,
                        isFirstItemPart && styles.firstItemPartNoTopBorder
                    )}>
                    <span className={styles.itemPartTime}>{timeText}</span>
                    {this.renderIconBar()}
                </div>
            </>
        );
    }

    private renderSingleLineViewMailListItemPart() {
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        const senderName = item.From && item.From.Mailbox && item.From.Mailbox.Name;
        const timeText = getItemPartTimeText(item);
        const senderClasses = classNames(
            !item.IsRead ? styles.senderUnread : styles.sender,
            item.IsDraft && styles.draft
        );
        const isFirstItemPart = this.props.nodeId == getFirstNodeIdToShow(this.props.allNodeIds);
        const isSingleLineView =
            getUserConfiguration().UserOptions.GlobalReadingPanePositionReact !==
            ReadingPanePosition.Right;
        const isSenderImageOff = getIsBitSet(ListViewBitFlagsMasks.HideSenderImage);
        return (
            <>
                <div
                    className={
                        isSingleLineView && isSenderImageOff
                            ? styles.mailListItemPartFirstColumnSingleLineViewNoSenderImage
                            : styles.mailListItemPartFirstColumnSingleLineView
                    }>
                    {this.renderFirstColumnContent()}
                </div>
                <div
                    className={classNames(
                        styles.mailListItemPartSecondColumnSingleLineView,
                        isFirstItemPart && styles.firstItemPartNoTopBorder
                    )}>
                    <span className={senderClasses}>
                        {item.IsDraft ? strings.draftSenderPersonaPlaceholder : senderName}
                    </span>
                    {this.renderIconBar()}
                </div>
                <div
                    className={classNames(
                        styles.mailListItemPartThirdColumnSingleLineView,
                        isFirstItemPart && styles.firstItemPartNoTopBorder
                    )}>
                    <span className={styles.itemPartTime}>{timeText}</span>
                </div>
            </>
        );
    }

    private initializeMailListComputedState(props: MailListItemPartProps) {
        this.computedState = new MailListItemPartComputedState(props.tableViewId, props.nodeId);
    }

    private renderFirstColumnContent() {
        const checkBoxClassNames = classNames(
            styles.itemPartCheckboxContainer,
            this.computedState.isChecked && styles.showCheckbox
        );
        const busStopStateMap = getListViewStore().expandedConversationViewState.busStopStateMap;
        const busStopStateForNode = busStopStateMap.get(this.props.nodeId);
        const source = busStopStateToIconUrlConverter(busStopStateForNode);

        return (
            <>
                {this.computedState.showBusStop && (
                    <img className={styles.busStopContainer} src={source} />
                )}
                {this.computedState.showCheckbox && (
                    <div
                        onClick={evt => {
                            evt.stopPropagation();
                            mailListSelectionActionsV2.toggleSelectItemPart(
                                this.props.nodeId,
                                this.props.allNodeIds
                            );
                        }}>
                        <Check
                            className={checkBoxClassNames}
                            checked={this.computedState.isChecked}
                        />
                    </div>
                )}
            </>
        );
    }

    private renderIconBar() {
        return (
            <MailListItemPartIconBar
                nodeId={this.props.nodeId}
                tableViewId={this.props.tableViewId}
                isSingleLine={this.props.isSingleLine}
                tableViewSupportsFlagging={this.props.tableViewSupportsFlagging}
            />
        );
    }

    /**
     * The onContextMenu event handler which opens the context menu
     * @param evt the mouse event
     */
    private onItemPartContextMenu = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        evt.preventDefault();

        mailListSelectionActionsV2.onItemPartContextMenu(
            evt,
            this.props.nodeId,
            this.props.allNodeIds,
            this.props.tableViewId
        );
    };

    // In checked mode we have the parent container be draggable
    private canDrag = () => {
        return (
            getListViewStore().expandedConversationViewState.selectedNodeIds.indexOf(
                this.props.nodeId
            ) == -1
        );
    };

    private getDragData = (): MailListItemPartDragData => {
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);
        return {
            itemType: DraggableItemTypes.MailListItemPart,
            tableViewId: this.props.tableViewId,
            subjects: [item.Subject ? item.Subject : strings.noSubject],
            itemIds: [item.ItemId.Id],
            nodeIds: [this.props.nodeId],
        };
    };
    /**
     * Calculates and returns the background color css for this mail list item part
     */
    private calculateBackgroundColorClass(): string {
        const item: Message = getItemToShowFromNodeId(this.props.nodeId);

        if (this.computedState.isSelected) {
            return styles.selectedMailListItemColor;
        } else if (item && item.Flag && item.Flag.FlagStatus == 'Flagged') {
            return styles.flaggedMailListItemPartColor;
        } else {
            return styles.regularMailListItemPartColor;
        }
    }
}
